import { useState, useEffect } from 'react';
import {
  Users, Trophy, TrendingUp, Loader2, AlertTriangle, Play, Pause, Globe, Lock, X, Activity, Info,
  Eye, Link2, ShieldAlert,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import FeatureGate from '../components/FeatureGate';
import { InfoTip } from '../components/ui';
import {
  getCopyTradingLeaderboard, publishBotForCopy, followBot,
  getCopyTradingFollowing, getMyPositions, pauseCopyTrading,
  getCopyTradingMasterEarnings,
  listGridBots, listSmartTrades, listDcaSafetyBots,
  listDexGridBots, listDexDcaPlans, listDexSmartTrades,
  listDexTvWebhooks, listDexArbBots,
  getExchangeConnections,
} from '../services/api';

const BOT_TYPE_LABEL = {
  grid: 'Grid Bot',
  smart: 'Smart Trade',
  dca: 'DCA Safety',
  dex_grid: 'DEX Grid',
  dex_dca: 'DEX DCA',
  dex_smart: 'DEX Smart Trade',
  dex_tv_webhook: 'DEX TV Webhook',
  dex_arbitrage: 'DEX Arbitrage',
};

const BOT_TYPE_COLOR = {
  grid:  'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  smart: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
  dca:   'text-violet-400 bg-violet-500/10 border-violet-500/30',
  dex_grid:        'text-cyan-400 bg-cyan-500/10 border-cyan-500/30',
  dex_dca:         'text-cyan-400 bg-cyan-500/10 border-cyan-500/30',
  dex_smart:       'text-cyan-400 bg-cyan-500/10 border-cyan-500/30',
  dex_tv_webhook:  'text-cyan-400 bg-cyan-500/10 border-cyan-500/30',
  dex_arbitrage:   'text-cyan-400 bg-cyan-500/10 border-cyan-500/30',
};

// Used to badge cards as wallet-connected (DEX) vs exchange-API (CEX) and to
// short-circuit the Follow button on the leaderboard (DEX is browse-only in
// Copy Trading v1 — see project_dex_parity_post_launch.md).
function isDexBotType(t) { return typeof t === 'string' && t.startsWith('dex_'); }

function fmt(n, dp = 2) {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return Number(n).toFixed(dp);
}

export default function CopyTradingPage() {
  const [tab, setTab] = useState('leaderboard');
  const [leaderboard, setLeaderboard] = useState([]);
  const [following, setFollowing] = useState([]);
  const [positions, setPositions] = useState([]);
  const [myBots, setMyBots] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [masterEarnings, setMasterEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [followTarget, setFollowTarget] = useState(null); // bot row from leaderboard

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [lb, fol, pos, gridR, smartR, dcaR, dexGridR, dexDcaR, dexSmartR, dexTvR, dexArbR, conns, earnings] = await Promise.all([
        getCopyTradingLeaderboard().then(r => r?.bots || []).catch(() => []),
        getCopyTradingFollowing().then(r => r?.following || []).catch(() => []),
        getMyPositions().then(r => r?.positions || []).catch(() => []),
        listGridBots().then(r => (r?.data || []).map(b => ({ ...b, botType: 'grid' }))).catch(() => []),
        listSmartTrades().then(r => (r?.data || []).map(t => ({ ...t, botType: 'smart', botId: t.tradeId }))).catch(() => []),
        listDcaSafetyBots().then(r => (r?.data || []).map(b => ({ ...b, botType: 'dca' }))).catch(() => []),
        // DEX bot fetches — each maps the engine's native id field to a shared
        // `botId` so the rest of the page can treat them uniformly. The
        // backend's loadBotConfig knows how to look each up by botType.
        listDexGridBots().then(r => (r?.data || r?.bots || []).map(b => ({
          ...b, botType: 'dex_grid',
          botId: b.gridBotId || b.botId,
          name: b.name || (b.pair ? `${b.pair.sellSymbol}↔${b.pair.buySymbol}` : null),
          symbol: b.pair?.sellSymbol && b.pair?.buySymbol ? `${b.pair.sellSymbol}/${b.pair.buySymbol}` : null,
        }))).catch(() => []),
        listDexDcaPlans().then(r => (r?.data || r?.plans || []).map(p => ({
          ...p, botType: 'dex_dca',
          botId: p.planId,
          name: p.name || `DCA ${p.sellSymbol}→${p.buySymbol}`,
          symbol: p.sellSymbol && p.buySymbol ? `${p.sellSymbol}/${p.buySymbol}` : null,
        }))).catch(() => []),
        listDexSmartTrades().then(r => (r?.data || r?.trades || []).map(t => ({
          ...t, botType: 'dex_smart',
          botId: t.tradeId,
          name: t.name || `Smart ${t.sellSymbol}→${t.buySymbol}`,
          symbol: t.sellSymbol && t.buySymbol ? `${t.sellSymbol}/${t.buySymbol}` : null,
        }))).catch(() => []),
        listDexTvWebhooks().then(r => (r?.data || r?.webhooks || []).map(w => ({
          ...w, botType: 'dex_tv_webhook',
          botId: w.webhookKey,
          name: w.name || `TV ${w.pair?.sellSymbol}↔${w.pair?.buySymbol}`,
          symbol: w.pair?.sellSymbol && w.pair?.buySymbol ? `${w.pair.sellSymbol}/${w.pair.buySymbol}` : null,
        }))).catch(() => []),
        listDexArbBots().then(r => (r?.data || r?.bots || []).map(b => ({
          ...b, botType: 'dex_arbitrage',
          botId: b.arbBotId || b.botId,
          name: b.name || `Arb ${b.pair?.sellSymbol}↔${b.pair?.buySymbol}`,
          symbol: b.pair?.sellSymbol && b.pair?.buySymbol ? `${b.pair.sellSymbol}/${b.pair.buySymbol}` : null,
        }))).catch(() => []),
        getExchangeConnections().then(r => (r?.exchanges || []).filter(c => c.connected)).catch(() => []),
        getCopyTradingMasterEarnings().catch(() => null),
      ]);
      setLeaderboard(lb);
      setFollowing(fol);
      setPositions(pos);
      setMyBots([...gridR, ...smartR, ...dcaR, ...dexGridR, ...dexDcaR, ...dexSmartR, ...dexTvR, ...dexArbR]);
      setExchanges(Array.isArray(conns) ? conns : []);
      setMasterEarnings(earnings);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  async function togglePublic(bot, isCurrentlyPublic) {
    try {
      const displayName = bot.name
        || (bot.botType === 'smart' && bot.symbol ? `Smart ${bot.symbol}${bot.side ? ` (${bot.side})` : ''}` : null)
        || bot.symbol
        || bot.pair
        || null;
      await publishBotForCopy(bot.botType, bot.botId, !isCurrentlyPublic, displayName);
      await load();
    } catch (err) { alert(err.message); }
  }

  async function handleUnfollow(botRef) {
    if (!window.confirm('Stop copying this trader? Any open positions on your exchange will remain — you can close them manually.')) return;
    try { await followBot(botRef, { action: 'unfollow' }); await load(); }
    catch (err) { alert(err.message); }
  }

  async function handlePauseResume(botRef, action) {
    try { await pauseCopyTrading(botRef, action); await load(); }
    catch (err) { alert(err.message); }
  }

  async function submitFollow(params) {
    try {
      await followBot(followTarget.botRef, { action: 'follow', ...params });
      setFollowTarget(null);
      await load();
    } catch (err) { alert(err.message); }
  }

  return (
    <FeatureGate feature="copyTradingWatchOnly">
      <div className="space-y-5 animate-fade-in max-w-7xl">
        <header>
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-7 h-7 text-amber-400" />
            <h1 className="text-2xl font-bold text-white">Copy Trading</h1>
            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">Live execution</span>
          </div>
          <p className="text-sm text-gray-400">
            Copy real trades from top-performing bots onto your own exchange account. <span className="text-emerald-400">Proportional sizing.</span> <span className="text-amber-400">User-set drawdown stop-out.</span> <span className="text-emerald-400">10% profit-share to the master on positive round-trips.</span>
          </p>
        </header>

        <div className="bg-red-500/5 border border-red-500/20 rounded-xl px-4 py-3 flex items-start gap-3">
          <ShieldAlert className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-red-300/90">
            <strong className="text-red-400">Real trades. Real money. Real risk.</strong> Following a trader authorises haythix to place real orders on your connected exchange when the master's bot fires. Past performance does not guarantee future returns — masters can lose money, and you will lose alongside them. Set a drawdown threshold you can actually tolerate.
          </div>
        </div>

        <div className="flex gap-2 border-b border-dark-700/50">
          {[
            { id: 'leaderboard', label: 'Leaderboard', count: leaderboard.length },
            { id: 'my-public',   label: 'My Public Bots', count: leaderboard.filter(b => b.isOwn).length },
            { id: 'following',   label: 'Copying', count: positions.length },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                tab === t.id ? 'border-amber-400 text-white' : 'border-transparent text-gray-500 hover:text-white'
              }`}>
              {t.label} {t.count > 0 && <span className="ml-1 text-xs text-gray-500">({t.count})</span>}
            </button>
          ))}
        </div>

        {error && (
          <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-3 text-red-400 text-sm flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" /> {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>
        ) : tab === 'leaderboard' ? (
          <LeaderboardView bots={leaderboard} onFollowClick={setFollowTarget} following={following} earnings={masterEarnings} />
        ) : tab === 'my-public' ? (
          <MyPublicView leaderboard={leaderboard} myBots={myBots} onToggle={togglePublic} earnings={masterEarnings} />
        ) : (
          <FollowingView positions={positions} onUnfollow={handleUnfollow} onPauseResume={handlePauseResume} />
        )}

        <div className="bg-dark-800/30 rounded-2xl border border-dark-700/30 p-4">
          <p className="text-[10px] text-gray-500 flex items-start gap-1.5">
            <Info className="w-3 h-3 flex-shrink-0 mt-0.5" />
            How it works: Master's bot fires a trade → fan-out worker (runs every 30s) places a proportional trade on each follower's connected exchange, sized by allocation. Closes auto-mirror. If your cumulative loss vs allocation crosses your drawdown threshold, you are auto-unfollowed and notified. On profitable round-trips, 10% of your gain is paid to the master, 1% to the platform. Privacy: only metrics + display name are shared between users — exchange keys + balances stay private.
          </p>
        </div>
      </div>

      {followTarget && (
        <FollowModal
          bot={followTarget}
          exchanges={exchanges}
          onCancel={() => setFollowTarget(null)}
          onSubmit={submitFollow}
        />
      )}
    </FeatureGate>
  );
}

function LeaderboardView({ bots, onFollowClick, following, earnings }) {
  if (!bots || bots.length === 0) {
    return (
      <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-12 text-center">
        <Trophy className="w-12 h-12 text-gray-600 mx-auto mb-3" />
        <p className="text-white font-medium mb-1">No public bots yet</p>
        <p className="text-sm text-gray-500">Bot owners need to opt in. If you own bots, switch to <span className="text-amber-400">My Public Bots</span> to share yours.</p>
      </div>
    );
  }
  const followedSet = new Set(following.map(f => f.botRef));
  // Map from botRef → master's earnings on that bot (only contains owned bots
  // when earnings.breakdown is present)
  const earningsByBotRef = new Map(
    Array.isArray(earnings?.breakdown) ? earnings.breakdown.map(b => [b.botRef, b]) : []
  );
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {bots.map(b => {
        const isProfit = (b.roi30d ?? 0) >= 0;
        const followed = followedSet.has(b.botRef);
        const typeStyle = BOT_TYPE_COLOR[b.botType] || BOT_TYPE_COLOR.grid;
        return (
          <div key={b.botRef} className="bg-dark-800 rounded-2xl border border-dark-700/50 p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-amber-400 font-mono text-xs">#{b.rank}</span>
                  <h3 className="text-white font-bold truncate">{b.displayName}</h3>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${typeStyle}`}>{BOT_TYPE_LABEL[b.botType] || b.botType}</span>
                  {b.isOwn && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">YOUR BOT</span>}
                </div>
                <p className="text-xs text-gray-500 font-mono">{b.exchange && `${b.exchange} · `}{b.symbol || '—'} · {b.followerCount} {b.followerCount === 1 ? 'copier' : 'copiers'}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-xs">
              <Stat label="30d ROI" value={`${b.roi30d >= 0 ? '+' : ''}${fmt(b.roi30d)}%`} color={isProfit ? 'text-emerald-400' : 'text-red-400'} />
              <Stat label="Win rate" value={`${b.winRate ?? 0}%`} />
              <Stat label="Trades" value={b.totalTrades ?? 0} />
              <Stat label="Max DD" value={`-${fmt(b.maxDrawdownPct)}%`} color="text-red-400" />
            </div>
            {b.isOwn ? (
              (() => {
                const e = earningsByBotRef.get(b.botRef);
                return e && e.masterEarnings > 0 ? (
                  <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-2 text-[10px] text-center">
                    <span className="text-gray-500">You've earned</span> <span className="text-emerald-400 font-bold font-mono">+${fmt(e.masterEarnings)}</span> <span className="text-gray-500">from</span> <span className="text-white font-mono">{e.uniqueFollowers}</span> <span className="text-gray-500">{e.uniqueFollowers === 1 ? 'copier' : 'copiers'}</span>
                  </div>
                ) : (
                  <div className="bg-dark-700/30 rounded-lg p-2 text-[10px] text-gray-500 text-center">Your bot — you can't copy yourself</div>
                );
              })()
            ) : followed ? (
              <button disabled className="w-full px-3 py-2 rounded-lg text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 cursor-default flex items-center justify-center gap-1">
                <Eye className="w-3 h-3" /> Copying — see "Copying" tab
              </button>
            ) : isDexBotType(b.botType) ? (
              <button disabled
                className="w-full px-3 py-2 rounded-lg text-xs bg-cyan-500/10 text-cyan-300 border border-cyan-500/30 cursor-default flex items-center justify-center gap-1"
                title="DEX bots are browse-only in Copy Trading v1 — auto-follow execution for wallet-connected strategies ships in a follow-up release."
              >
                <Eye className="w-3 h-3" /> DEX · browse only
              </button>
            ) : (
              <button onClick={() => onFollowClick(b)} className="w-full px-3 py-2 rounded-lg text-xs bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 flex items-center justify-center gap-1">
                <Play className="w-3 h-3" /> Copy this trader
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

function MyPublicView({ leaderboard, myBots, onToggle, earnings }) {
  const hasEarningsHistory = earnings && (earnings.totalEarnings > 0 || earnings.totalTradesEarned > 0);
  if (!myBots || myBots.length === 0) {
    return (
      <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-12 text-center">
        <Globe className="w-12 h-12 text-gray-600 mx-auto mb-3" />
        <p className="text-white font-medium mb-1">No bots to publish yet</p>
        <p className="text-sm text-gray-500">Create a Grid Bot, Smart Trade, or DCA Safety first. Then opt in here to let others copy your real trades — you earn 10% of their profits.</p>
      </div>
    );
  }
  const publishedKeys = new Map(leaderboard.filter(b => b.isOwn).map(b => {
    const [bt,, id] = b.botRef.split(':');
    return [`${bt}:${id}`, b];
  }));
  return (
    <div className="space-y-3">
      {hasEarningsHistory && (
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-emerald-400 font-bold">Copy trading earnings</p>
              <p className="text-2xl font-bold text-emerald-400 mt-0.5 font-mono">${fmt(earnings.totalEarnings)}</p>
            </div>
            <div className="grid grid-cols-3 gap-3 text-xs">
              <div>
                <p className="text-[10px] text-gray-500">Profitable trades</p>
                <p className="text-white font-bold font-mono">{earnings.totalTradesEarned}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-500">Followers' gross profit</p>
                <p className="text-white font-bold font-mono">${fmt(earnings.totalGrossProfit)}</p>
              </div>
              <div>
                <p className="text-[10px] text-gray-500">Fee rate</p>
                <p className="text-white font-bold font-mono">{earnings.feeRatePct}%</p>
              </div>
            </div>
          </div>
          {Array.isArray(earnings.breakdown) && earnings.breakdown.length > 0 && (
            <div className="mt-3 pt-3 border-t border-emerald-500/10">
              <p className="text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">By bot</p>
              <div className="space-y-1">
                {earnings.breakdown.slice(0, 5).map(b => (
                  <div key={b.botRef} className="flex items-center justify-between text-xs font-mono">
                    <span className="text-gray-300 truncate">{b.botRef}</span>
                    <span className="text-gray-500">{b.uniqueFollowers} {b.uniqueFollowers === 1 ? 'follower' : 'followers'}</span>
                    <span className="text-emerald-400 font-bold">+${fmt(b.masterEarnings)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      <p className="text-xs text-gray-500">Toggle individual bots between private and public. When you publish a bot, other users can copy its real trades onto their own exchange. You earn 10% of every profitable round-trip they make. Privacy: only metrics + display name are shared — your exchange identity and balances stay private.</p>
      {myBots.map(b => {
        const key = `${b.botType}:${b.botId}`;
        const lb = publishedKeys.get(key);
        const published = !!lb;
        const typeStyle = BOT_TYPE_COLOR[b.botType] || BOT_TYPE_COLOR.grid;
        return (
          <div key={key} className="bg-dark-800 rounded-2xl border border-dark-700/50 p-4 flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                <h3 className="text-white font-bold truncate">{b.name || b.symbol || b.botId?.slice(0, 8)}</h3>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${typeStyle}`}>{BOT_TYPE_LABEL[b.botType] || b.botType}</span>
                {published
                  ? <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">PUBLIC</span>
                  : <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-500/15 text-gray-400 border border-gray-500/30">PRIVATE</span>}
              </div>
              <p className="text-xs text-gray-500 font-mono">{b.exchangeId} · {b.symbol || b.pair || ''}</p>
              {published && lb && (
                <p className="text-[10px] text-emerald-400/80 mt-1">Shared: ROI {lb.roi30d >= 0 ? '+' : ''}{fmt(lb.roi30d)}% · {lb.followerCount} {lb.followerCount === 1 ? 'copier' : 'copiers'}</p>
              )}
            </div>
            <button onClick={() => onToggle(b, published)}
              className={`px-4 py-2 rounded-lg text-xs flex items-center gap-1 whitespace-nowrap ${
                published
                  ? 'bg-gray-500/10 text-gray-400 border border-gray-500/30 hover:bg-gray-500/20'
                  : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/20'
              }`}>
              {published ? <><Lock className="w-3 h-3" /> Make Private</> : <><Globe className="w-3 h-3" /> Make Public</>}
            </button>
          </div>
        );
      })}
    </div>
  );
}

function FollowingView({ positions, onUnfollow, onPauseResume }) {
  if (!positions || positions.length === 0) {
    return (
      <div className="bg-dark-800 rounded-2xl border border-dark-700/50 p-12 text-center">
        <Activity className="w-12 h-12 text-gray-600 mx-auto mb-3" />
        <p className="text-white font-medium mb-1">Not copying anyone yet</p>
        <p className="text-sm text-gray-500">Browse the leaderboard and click <span className="text-amber-400">Copy this trader</span> on a bot you want to copy.</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {positions.map(p => {
        const isProfit = (p.realizedPnlUsd ?? 0) >= 0;
        const typeStyle = BOT_TYPE_COLOR[p.botType] || BOT_TYPE_COLOR.grid;
        const stopped = p.status === 'stopped_drawdown';
        const paused = p.status === 'paused';
        return (
          <div key={p.botRef} className={`bg-dark-800 rounded-2xl border p-5 ${stopped ? 'border-red-500/30' : paused ? 'border-amber-500/30' : 'border-dark-700/50'}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h3 className="text-white font-bold truncate">{p.displayName}</h3>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${typeStyle}`}>{BOT_TYPE_LABEL[p.botType] || p.botType}</span>
                  {stopped && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 border border-red-500/30">DRAWDOWN STOP</span>}
                  {paused && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">PAUSED</span>}
                </div>
                <p className="text-xs text-gray-500">
                  Copying on <span className="text-gray-300 font-mono">{p.exchangeId}</span> · ${p.allocationUsd.toFixed(2)} budget · DD stop {p.maxDrawdownPct}%
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-xs">
              <Stat label="Net P&L (after fees)" value={`${isProfit ? '+' : ''}$${fmt(p.realizedPnlUsd)}`} color={isProfit ? 'text-emerald-400' : 'text-red-400'} />
              <Stat label="Open exposure" value={`$${fmt(p.openPositionUsd)}`} />
              <Stat label="Current DD" value={`${fmt(p.currentDrawdownPct)}%`} color={p.currentDrawdownPct > 0 ? 'text-red-400' : 'text-gray-400'} />
              <Stat label="Copy trades" value={p.copyTradesExecuted ?? 0} />
            </div>

            {/* Fee breakdown — only render if user has any realized fees */}
            {(p.totalGrossProfit > 0 || p.totalMasterFeesPaid > 0) && (
              <div className="bg-dark-700/30 rounded-lg p-2 mb-3 text-[10px] space-y-0.5">
                <div className="flex justify-between">
                  <span className="text-gray-500">Gross profit</span>
                  <span className="text-emerald-400 font-mono">+${fmt(p.totalGrossProfit)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Master fees (10%)</span>
                  <span className="text-amber-400 font-mono">-${fmt(p.totalMasterFeesPaid)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Platform fees (1%)</span>
                  <span className="text-amber-400 font-mono">-${fmt(p.totalPlatformFeesPaid)}</span>
                </div>
              </div>
            )}

            {/* Recent executions feed */}
            {Array.isArray(p.recentExecutions) && p.recentExecutions.length > 0 && (
              <div className="bg-dark-700/20 rounded-lg p-2 mb-3">
                <p className="text-[9px] uppercase tracking-wider text-gray-500 font-bold mb-1">Recent activity</p>
                <div className="space-y-0.5">
                  {p.recentExecutions.slice(0, 3).map((e, i) => (
                    <div key={i} className="flex items-center justify-between text-[10px] font-mono">
                      <span className={`flex items-center gap-1 ${e.success ? 'text-emerald-400' : e.error ? 'text-red-400' : 'text-gray-500'}`}>
                        {e.success ? '✓' : e.error ? '✕' : '−'}
                        {e.success && e.side ? `${e.side.toUpperCase()} ${e.symbol || ''}` :
                         e.skipped ? `skipped: ${e.reason || ''}` :
                         e.error ? `error: ${String(e.error).slice(0, 30)}` : '—'}
                      </span>
                      <span className="text-gray-500">
                        {e.success && e.filledUsd ? `$${fmt(e.filledUsd)}` :
                         e.realizedPnlUsd != null ? `${e.realizedPnlUsd >= 0 ? '+' : ''}$${fmt(e.realizedPnlUsd)}` : ''}
                      </span>
                      <span className="text-gray-600 text-[9px]">{e.ts ? new Date(e.ts).toLocaleTimeString() : ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {stopped && (
              <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-2 mb-2 text-[11px] text-red-300/90">
                Auto-stopped after your loss crossed the threshold. Any open positions remain on your exchange — close them manually. To resume copying this trader, click <strong>Stop copying</strong> and start a fresh follow with new settings.
              </div>
            )}
            <div className="flex gap-2">
              {!stopped && (
                paused ? (
                  <button onClick={() => onPauseResume(p.botRef, 'resume')}
                    className="flex-1 px-3 py-2 rounded-lg text-xs bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 flex items-center justify-center gap-1">
                    <Play className="w-3 h-3" /> Resume
                  </button>
                ) : (
                  <button onClick={() => onPauseResume(p.botRef, 'pause')}
                    className="flex-1 px-3 py-2 rounded-lg text-xs bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 flex items-center justify-center gap-1">
                    <Pause className="w-3 h-3" /> Pause
                  </button>
                )
              )}
              <button onClick={() => onUnfollow(p.botRef)}
                className={`${stopped ? 'w-full' : 'flex-1'} px-3 py-2 rounded-lg text-xs bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 flex items-center justify-center gap-1`}>
                <X className="w-3 h-3" /> Stop copying
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FollowModal({ bot, exchanges, onCancel, onSubmit }) {
  const [exchangeId, setExchangeId] = useState(exchanges[0]?.exchangeId || '');
  const [allocationUsd, setAllocationUsd] = useState('100');
  const [maxDrawdownPct, setMaxDrawdownPct] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const hasExchanges = exchanges.length > 0;
  const allocNum = parseFloat(allocationUsd);
  const ddNum = parseFloat(maxDrawdownPct);
  const allocValid = Number.isFinite(allocNum) && allocNum >= 20;
  const ddValid = Number.isFinite(ddNum) && ddNum > 0 && ddNum <= 100;

  // Net-profit projection helper — illustrative only, based on master's 30d ROI
  let netProjection = null;
  if (allocValid && Number.isFinite(bot.roi30d)) {
    const gross = allocNum * (bot.roi30d / 100);
    const fees = gross > 0 ? gross * 0.11 : 0; // 10% master + 1% platform
    const net = gross - fees;
    netProjection = { gross, fees, net };
  }

  async function handleSubmit() {
    if (!exchangeId) { setError('Select an exchange where trades will execute.'); return; }
    if (!Number.isFinite(allocNum) || allocNum <= 0) { setError('Allocation must be greater than zero.'); return; }
    if (allocNum < 20) { setError('Minimum allocation is $20 to clear exchange minimum order sizes.'); return; }
    if (!ddValid) { setError('Drawdown stop must be between 1 and 100 (percent).'); return; }
    setSaving(true);
    setError('');
    try {
      await onSubmit({ exchangeId, allocationUsd: allocNum, maxDrawdownPct: ddNum });
    } catch (err) {
      setError(err?.message || 'Failed to start copy trading.');
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-dark-800 border border-dark-600 rounded-2xl w-full max-w-md p-5 space-y-4 my-8">
        <div>
          <h3 className="text-base font-bold text-white">Start copying {bot.displayName}</h3>
          <p className="text-xs text-gray-500 mt-1">{BOT_TYPE_LABEL[bot.botType] || bot.botType}{bot.symbol ? ` · ${bot.symbol}` : ''}{bot.exchange ? ` · ${bot.exchange}` : ''}</p>
        </div>

        {/* Master stats recap */}
        <div className="bg-dark-700/40 rounded-lg p-3 grid grid-cols-4 gap-2 text-center">
          <div>
            <p className="text-[9px] uppercase tracking-wider text-gray-500">30d ROI</p>
            <p className={`text-sm font-bold font-mono ${(bot.roi30d ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {bot.roi30d >= 0 ? '+' : ''}{fmt(bot.roi30d)}%
            </p>
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-wider text-gray-500">Win rate</p>
            <p className="text-sm font-bold font-mono text-white">{bot.winRate ?? 0}%</p>
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-wider text-gray-500">Trades</p>
            <p className="text-sm font-bold font-mono text-white">{bot.totalTrades ?? 0}</p>
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-wider text-gray-500">Max DD</p>
            <p className="text-sm font-bold font-mono text-red-400">-{fmt(bot.maxDrawdownPct)}%</p>
          </div>
        </div>

        {!hasExchanges && (
          <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-3 flex items-start gap-2">
            <Link2 className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-red-300">
              You don't have any exchanges connected. <Link to="/settings" className="text-red-400 underline">Connect one</Link> with trade permission first.
            </div>
          </div>
        )}

        {hasExchanges && (
          <>
            <div>
              <label className="text-xs text-gray-400 mb-1 block">Exchange where copy trades will execute</label>
              <select value={exchangeId} onChange={e => setExchangeId(e.target.value)}
                className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-sm">
                {exchanges.map(c => (
                  <option key={c.exchangeId} value={c.exchangeId}>{c.exchangeId} {c.label ? `(${c.label})` : ''}</option>
                ))}
              </select>
              <p className="text-[10px] text-gray-500 mt-1">Make sure this account has enough free balance and the master's symbol{bot.symbol ? ` (${bot.symbol})` : ''} is supported.</p>
            </div>

            <div>
              <label className="text-xs text-gray-400 mb-1 flex items-center gap-1">
                Total allocation (USD) — minimum $20
                <InfoTip side="right">Scaling ratio = your allocation ÷ master's capital. $200+ recommended in practice — mirror-fills smaller than exchange minimum get rejected.</InfoTip>
              </label>
              <input type="number" min="20" step="any" value={allocationUsd}
                onChange={e => setAllocationUsd(e.target.value)}
                className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-sm font-mono"
                placeholder="100" />
              <p className="text-[10px] text-gray-500 mt-1">The real capital you commit to this copy. Per-trade cap: 50% of allocation per single copy buy (protects against runaway single trades). Open exposure never exceeds total allocation.</p>
            </div>

            <div>
              <label className="text-xs text-gray-400 mb-1 flex items-center gap-1">
                Drawdown stop-out (%) — required
                <InfoTip side="right">Auto-unfollow trigger. 10% = conservative (frequent stop-outs in volatility), 20% = default, 30%+ = aggressive (bigger blowups). Must be set, no default.</InfoTip>
              </label>
              <input type="number" min="1" max="100" step="any" value={maxDrawdownPct}
                onChange={e => setMaxDrawdownPct(e.target.value)}
                className="w-full bg-dark-700 border border-dark-600 rounded-lg p-2 text-white text-sm font-mono"
                placeholder="e.g. 20" />
              <p className="text-[10px] text-gray-500 mt-1">If your cumulative loss vs allocation hits this percent, you are auto-unfollowed. Common: 10% (conservative), 20% (moderate), 30%+ (aggressive). You must set this — no default.</p>
            </div>

            {/* Net projection */}
            {netProjection && (
              <div className="bg-dark-700/40 rounded-lg p-3 text-[11px] space-y-1">
                <p className="text-[9px] uppercase tracking-wider text-gray-500 font-bold mb-1">If their 30d ROI repeated on your allocation</p>
                <div className="flex justify-between">
                  <span className="text-gray-400">Gross profit (illustrative)</span>
                  <span className={`font-mono ${netProjection.gross >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {netProjection.gross >= 0 ? '+' : ''}${fmt(netProjection.gross)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Fees (10% master + 1% platform)</span>
                  <span className="font-mono text-amber-400">-${fmt(Math.abs(netProjection.fees))}</span>
                </div>
                <div className="flex justify-between border-t border-dark-600 pt-1 mt-1">
                  <span className="text-gray-300 font-bold">You'd keep (net)</span>
                  <span className={`font-mono font-bold ${netProjection.net >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {netProjection.net >= 0 ? '+' : ''}${fmt(netProjection.net)}
                  </span>
                </div>
                <p className="text-[9px] text-gray-600 italic mt-1">Past performance does not guarantee future returns. Losses are possible.</p>
              </div>
            )}

            <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-3 text-[11px] text-amber-300/90 space-y-1">
              <p><strong className="text-amber-400">Fees:</strong> 10% of profit to master + 1% to platform, on positive round-trips only. No fees on losses.</p>
              <p><strong className="text-amber-400">Stale skip:</strong> If a master trade is more than 5 minutes old when we process it (e.g. worker delay), it's skipped — price has moved too much.</p>
            </div>

            {error && (
              <div className="bg-red-500/5 border border-red-500/20 rounded-lg p-2 text-red-400 text-xs">{error}</div>
            )}
          </>
        )}

        <div className="flex gap-2 pt-2">
          <button onClick={onCancel} className="flex-1 px-3 py-2 rounded-lg border border-dark-600 text-gray-300 hover:bg-dark-700 text-sm">Cancel</button>
          <button onClick={handleSubmit} disabled={!hasExchanges || saving || !allocValid || !ddValid}
            className="flex-1 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:bg-dark-600 disabled:text-gray-500 text-black font-semibold text-sm flex items-center justify-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
            {saving ? 'Starting…' : 'Start copying'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="bg-dark-700/40 rounded-lg p-2">
      <p className="text-[10px] uppercase tracking-wider text-gray-500">{label}</p>
      <p className={`font-bold font-mono mt-0.5 ${color || 'text-white'}`}>{value}</p>
    </div>
  );
}
